import { useDispatch, useSelector } from "react-redux";
import { getCurrentStep, getFitReadyState } from "./redux/selectors";
import { MainScreen } from "./views/MainScreen/MainScreen";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { toolbarHeightPx } from "./config";
import LoadingScreen from "./views/LoadingScreen";
import { goNextFrame, goPreviousFrame, toggleHidden } from "./redux";
import { useKey } from "react-use";
import throttle from "lodash/throttle";
import { steps } from "./util/constants";
import { Notifier } from "./components/Notifier/Notifier";
import { ErrorBoundary } from "react-error-boundary";
import React, { useEffect } from "react";
import { InitialScreen } from "./views/InitialScreen/InitialScreen";
import { ActionCreators as UndoActionCreators } from "redux-undo";
import { VideoTutorial } from "./components/VideoTutorial/VideoTutorial";
import { ConfigProvider } from "antd";

const theme = createTheme({
  zIndex: {
    modal: 2147483648,
  },
  palette: {
    primary: {
      main: "#2586da",
    },
    secondary: {
      main: "#7e7e7e",
    },
  },
  typography: {
    allVariants: {
      fontFamily: `"Inter", Arial, Helvetica, sans-serif`,
      color: "#373a3c",
    },
    button: {
      textTransform: "none",
    },
    h2: {
      fontSize: "36px",
    },
    h3: {
      fontSize: "16px",
      fontWeight: 700,
      color: "#242423",
      lineHeight: "20px",
    },
    h4: {
      fontSize: "15px",
      color: "#5c6164",
      fontWeight: 700,
    },
    h5: {
      fontSize: "13px",
      color: "#747a7f;",
    },
    inputText: {
      fontSize: "13px",
    },
    body1: {
      fontSize: "14px",
      lineHeight: "16px",
    },
  },
  overrides: {
    MuiButton: {
      label: {
        fontSize: "1rem",
      },
    },
    MuiIconButton: {
      root: {
        borderRadius: "var(--border-radius-small, 5px)",
        padding: "7px",
      },
    },
    MuiSvgIcon: {
      root: {
        fontSize: "14px",
        color: "var(--sirv-com-dark)",
      },
    },
  },
  props: {
    MuiTextField: {
      variant: "outlined",
      size: "small",
    },
    MuiTooltip: {
      enterDelay: 400,
    },
  },
  mixins: {
    toolbar: {
      minHeight: toolbarHeightPx,
    },
  },
});

function ErrorFallback({ error, resetErrorBoundary }) {
  return (
    <div role="alert">
      <p>The following error has ocurred in the Hotspot Editor: </p>
      <pre>{error.message}</pre>
      <p>Please refresh the page or navigate back</p>
    </div>
  );
}

function App() {
  const step = useSelector(getCurrentStep);
  const fitReady = useSelector(getFitReadyState);
  const currentStepToContentMap = {
    [steps.init]: <InitialScreen />,
    [steps.loading]: <LoadingScreen />,
    [steps.configureLocations]: <MainScreen />,
    [steps.configurePointerText]: <MainScreen />,
    [steps.configureHoverAction]: <MainScreen />,
    [steps.configureClickAction]: <MainScreen />,
    [steps.preview]: <MainScreen />,
    [steps.pickTheme]: <MainScreen />,
    [steps.advanced]: <MainScreen />,
    [steps.SMVPreview]: <MainScreen />,
  };

  // Handle arrow keys
  // Placing it here and not lower closer to the <Hotspot />
  // because when re-rendering a compo useKey creates a new listener
  // effectively making it impossible to throttle actions
  const dispatch = useDispatch();
  const definedKeys = {
    ArrowLeft: "ArrowLeft",
    ArrowRight: "ArrowRight",
    " ": " ",
  };
  const handleArrows = (e) => {
    const key = e.key;
    if (!(key in definedKeys)) {
      return;
    }
    if (document.activeElement.tagName !== "BODY") {
      return false;
    }
    switch (key) {
      case definedKeys.ArrowLeft:
        dispatch(goNextFrame());
        break;
      case definedKeys.ArrowRight:
        dispatch(goPreviousFrame());
        break;
      case definedKeys[" "]:
        if (step !== steps.configureLocations) {
          return;
        }
        if (fitReady) {
          dispatch(toggleHidden());
        }
        break;
      default:
        throw new Error("This code must be unreachable");
    }
  };
  useEffect(() => {
    const handleKeyUp = (event) => {
      if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
        // Dispatch the FLASH_HOTSPOT_PATH action
        dispatch({ type: "FLASH_HOTSPOT_PATH" });
      }
    };

    // Add the event listener for keyup
    window.addEventListener("keyup", handleKeyUp);

    // Remove the event listener when the component is unmounted
    return () => {
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [dispatch]); // Include dispatch in the dependency array
  useKey(
    Object.keys(definedKeys),
    throttle((e) => handleArrows(e), 160),
    {}
  );
  useKey(
    //undo
    (e) => {
      const ctrlZPressed =
        e.ctrlKey && !e.metaKey && !e.shiftKey && e.key === "z";
      if (ctrlZPressed) {
        e.preventDefault();
      }
      return ctrlZPressed;
    },
    () => dispatch(UndoActionCreators.undo())
  );
  useKey(
    //undo
    (e) => {
      const ctrlYPressed =
        e.ctrlKey && !e.metaKey && !e.shiftKey && e.key === "y";
      if (ctrlYPressed) {
        e.preventDefault();
      }
      return ctrlYPressed;
    },
    () => dispatch(UndoActionCreators.redo())
  );

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <ThemeProvider //for muiv4
        theme={theme}
      >
        <ConfigProvider //for antd
          theme={{
            token: {
              fontFamily: "Inter",
            },
          }}
        >
          {currentStepToContentMap[step]}
          <Notifier />
          <VideoTutorial />
        </ConfigProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
